import { Injectable, computed, inject, signal } from '@angular/core';
import { SessionStorageService } from './session-storage.service';
import type { UploadFileStatus } from '../models/upload.model';

const STORAGE_KEY = 'attachments_uploads';


@Injectable({ providedIn: 'root' })
export class UploadHistoryStore {
  private readonly sessionStorage = inject(SessionStorageService);

  readonly uploads = signal<UploadFileStatus[]>(this.loadFromStorage());

  readonly visible = signal(false);
  readonly expanded = signal(true);

  readonly progress = signal(0);

  readonly isUploading = computed(() =>
    this.uploads().some((u) => u.status === 'pending' || u.status === 'uploading'),
  );

  readonly isComplete = computed(() => {
    const u = this.uploads();
    return u.length > 0 && u.every((x) => x.status === 'success' || x.status === 'error');
  });

  readonly panelTitle = computed(() => {
    const u = this.uploads();
    if (u.length === 0) return 'Feltöltés';
    const active = u.filter(
      (x) => x.status === 'pending' || x.status === 'uploading' || x.status === 'scanning',
    ).length;
    if (active > 0) return `${active} feltöltés folyamatban`;
    return `${u.filter((x) => x.status === 'success').length} feltöltés befejeződött`;
  });

  private batchOffset = 0;

  beginBatch(groups: UploadFileStatus[]): void {
    this.uploads.update((list) => {
      const incoming = new Set(groups.map((g) => g.id));
      const kept = list.filter((u) => !incoming.has(u.id));
      this.batchOffset = kept.length;
      return [...kept, ...groups];
    });
    this.persist();
  }

  updateGroup(index: number, patch: Partial<UploadFileStatus>): void {
    const target = this.batchOffset + index;
    this.uploads.update((list) => list.map((u, i) => (i === target ? { ...u, ...patch } : u)));
    this.persist();
  }

  updateChild(groupIndex: number, childIndex: number, patch: Partial<UploadFileStatus>): void {
    const target = this.batchOffset + groupIndex;
    this.uploads.update((list) =>
      list.map((u, i) => {
        if (i !== target || !u.children) return u;
        const children = u.children.map((c, j) => (j === childIndex ? { ...c, ...patch } : c));
        return { ...u, children };
      }),
    );
    this.persist();
  }


  markScanning(scanningNames: Set<string>): void {
    if (scanningNames.size === 0) return;
    this.uploads.update((list) =>
      list.map((u) => {
        if (u.children?.length) {
          const children = u.children.map((c) =>
            c.status === 'success' && scanningNames.has(c.name)
              ? { ...c, status: 'scanning' as const }
              : c,
          );
          const anyScanning = children.some((c) => c.status === 'scanning');
          return {
            ...u,
            children,
            status: anyScanning && u.status === 'success' ? ('scanning' as const) : u.status,
          };
        }
        return u.status === 'success' && scanningNames.has(u.name)
          ? { ...u, status: 'scanning' as const }
          : u;
      }),
    );
    this.persist();
  }

  markScanned(): void {
    const hasScanning = this.uploads().some(
      (u) => u.status === 'scanning' || u.children?.some((c) => c.status === 'scanning'),
    );
    if (!hasScanning) return;
    const settle = (s: UploadFileStatus): UploadFileStatus =>
      s.status === 'scanning' ? { ...s, status: 'success' } : s;
    this.uploads.update((list) =>
      list.map((u) => {
        const settled = settle(u);
        return settled.children?.length
          ? { ...settled, children: settled.children.map(settle) }
          : settled;
      }),
    );
    this.persist();
  }

  clear(): void {
    this.uploads.set([]);
    this.progress.set(0);
    this.visible.set(false);
    this.sessionStorage.removeItem(STORAGE_KEY);
  }

  private persist(): void {
    this.sessionStorage.setItem(STORAGE_KEY, this.uploads());
  }

  private loadFromStorage(): UploadFileStatus[] {
    const raw = this.sessionStorage.getItem(STORAGE_KEY) as UploadFileStatus[] | null;
    if (!Array.isArray(raw)) return [];
    const normalize = (s: UploadFileStatus): UploadFileStatus => {
      if (s.status === 'pending' || s.status === 'uploading') return { ...s, status: 'error' };
      if (s.status === 'scanning') return { ...s, status: 'success' };
      return s;
    };
    return raw.map((u) =>
      u.children?.length
        ? { ...normalize(u), children: u.children.map(normalize) }
        : normalize(u),
    );
  }
}
